import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { useBillingInfoQuery } from 'network/queries/useBillingInfoQuery';
import { isReversedPendingCheckout } from 'utils/billing/conversion';
import { getHasExistingSubscription } from 'components/Billing/ConversionPopup/utils';

/**
 * True while a reversed (post-cancel) account should still see the migration
 * discount / days-left UI: it's reversed-self-serve, the coupon is still live
 * (`migrationPerk.active`), and it hasn't bought a self-serve plan yet.
 *
 * The "hasn't bought" signal is billing-info's subscription state — the conversion
 * record stays terminal `reversed` after checkout, so it can't be the stop-signal.
 * Billing-info is fetched ONLY for reversed accounts (a tiny cohort) so this stays
 * off the app-wide fetch path for everyone else; it flips false the moment they buy.
 */
export const useShowsReversedMigrationOffer = (): boolean => {
  const { data: accountInfo } = useAccountInfo();
  const isReversed = isReversedPendingCheckout(accountInfo);
  const { data: billingInfo } = useBillingInfoQuery({ enabled: isReversed });

  // Require billing-info to have loaded before showing the offer — the conversion
  // record stays terminal `reversed`, so account-info reads "reversed" even after
  // purchase; treating the loading `undefined` as "no subscription" would flash the
  // offer on refresh until billing-info resolves.
  return (
    isReversed &&
    accountInfo?.migrationPerk?.active === true &&
    billingInfo !== undefined &&
    !getHasExistingSubscription(billingInfo.subscriptionState)
  );
};
